// @flow
import { httpClient } from 'angieslist-app-platform';
import _ from 'lodash';
import queryString from 'query-string';
import type { ServiceProvider } from '../../types';

export type GetCurrentUsersServiceProvidersRequest = {
  offset: number,
  limit: number,
  sortBy: 'alId' | 'name' | 'overall_gpa' | 'overall_reviews';
};

export type GetCurrentUsersServiceProvidersResponse = {
  serviceProviders: ServiceProvider[],
  offset: number,
  limit: number,
  total: number,
};

const getCurrentUsersServiceProviders = ({
  offset,
  limit,
  sortBy,
}: GetCurrentUsersServiceProvidersRequest): any =>
  httpClient.get(`/gateway/office-home/v2/users/current/service_providers?${queryString.stringify({
    offset,
    limit,
    sortBy,
  })}`);

export type GetServiceProviderRequest = number | string | number[] | string[];
export type GetServiceProvidersRequest = {
  sortBy: "name" | "ratings" | "reviews",
  offset: number,
  limit: number,
};

export type GetServiceProviderResponse = {
  serviceProviders: ServiceProvider[],
};

export type GetServiceProvidersResponse = {
  sortedBy: "name" | "ratings" | "reviews",
  serviceProviders: ServiceProvider[],
  totalCount: number,
};

export type GetServiceProviderMetricsRequest = number | string;
export type GetServiceProviderMetricsResponse = {
  serviceProviderId: number,
  leadsNew30Days: number,
  leadsWon30Days: number,
  leadsTotal30Days: number,
  messagesNew30Days: number,
  messagesUnanswered30Days: number,
  messagesTotal30Days: number,
  offersTotal: number,
  profileCompletion: number,
};

export type GetServiceProviderAccountOwnerRequest = number | string;
export type GetServiceProviderAccountOwnerResponse = {
  serviceProviderId: number,
  name: string,
  phone: string,
  email: string,
};

export type GetAdsServiceProvidersInfoRequest = number | string;

export type GetAdsServiceProvidersInfoResponse = {
  serviceProviderId: number,
  name: string,
  isNorthStar: boolean,
  isAdvertiser: boolean,
  isAgreementActiveAndOnlineEnroll: boolean
};

export type GetServiceProviderUnreadCountsRequest = GetServiceProviderRequest;
export type GetServiceProviderUnreadLeadsCountsRequest = GetServiceProviderRequest;
export type GetLeafSlugsRequest = GetServiceProviderRequest;

const getServiceProviderUnreadLeadsCounts =
  (serviceProviderIds: GetServiceProviderUnreadLeadsCountsRequest) =>
    httpClient.get(`/gateway/office-home/v1/service_provider/new_activity?${queryString.stringify({
      serviceProviderIds,
    })}`, {
      headers: {
        'X-ANGI-CallerId': serviceProviderIds,
      },
    }).then(response => response.body.unreadCounts.map(counts => ({
      id: counts.serviceProviderId,
      unreadDealCount: counts.deals,
      unreadQuoteCount: counts.quotesRequested,
      unreadLeadCount: counts.leads,
      unreadMessageCount: counts.unreadConversations,
    }))).catch(() => []);

const getServiceProviderUnreadReviewCount =
  (serviceProviderIds: GetServiceProviderUnreadCountsRequest) => {
    const ids = _.flatten([serviceProviderIds]);
    const requests = ids.map(id => httpClient.get(
      '/gateway/office-home/v1/review/unread',
      {
        headers: {
          'X-ANGI-CallerId': id,
        },
        params: {
          serviceProviderId: id,
        },
      },
    ).then(response => ({
      id,
      unreadReviewCount: response.body.unreadReviews,
    })).catch(() => null));
    return Promise.all(requests);
  };

const getServiceProviderUnreadCounts =
  (serviceProviderIds: GetServiceProviderUnreadCountsRequest):
    Promise<{| body: any|}> => Promise.all([
    getServiceProviderUnreadLeadsCounts(serviceProviderIds),
    getServiceProviderUnreadReviewCount(serviceProviderIds),
  ]).then(results => ({
    body: _.flatten(results).filter(x => x && x.id),
  }));

const getServiceProvider = (serviceProviderIds: GetServiceProviderRequest): any =>
  httpClient.get(`/gateway/office-home/v4/service_provider?${queryString.stringify({
    serviceProviderIds,
  })}`, {
    headers: {
      'X-ANGI-CallerId': serviceProviderIds,
    },
  });

const getServiceProviderBusinessInfo = (serviceProviderId: number, oneTimeToken: string): any =>
  httpClient.get(`/gateway/office-home/v1/service_provider/${serviceProviderId}/business_information`, {
    headers: {
      'X-ANGI-CallerId': serviceProviderId,
      Authorization: 'OneTime '.concat(oneTimeToken),
    },
  });

const getServiceProviderMetrics = (serviceProviderId: GetServiceProviderMetricsRequest): any =>
  httpClient.get(`/gateway/office-home/v1/service_providers/${serviceProviderId}/metrics`, {
    headers: {
      'X-ANGI-CallerId': serviceProviderId,
    },
  });

const getServiceProviderAccountOwner =
  (serviceProviderId: GetServiceProviderAccountOwnerRequest): any =>
    httpClient.get(`/gateway/office-home/v1/service_providers/${serviceProviderId}/account_owner`, {
      headers: {
        'X-ANGI-CallerId': serviceProviderId,
      },
    });

const getAdsServiceProviderInfo =
  (serviceProviderId: GetAdsServiceProvidersInfoRequest): any =>
    httpClient.get(`/gateway/office-home/v1/angiAds/${serviceProviderId}/info`, {
      headers: {
        'X-ANGI-CallerId': serviceProviderId,
      },
    });

const getLeafSlugs = (serviceProviderId: GetLeafSlugsRequest): any =>
  httpClient.get(`/gateway/spprofile-visitor/v1/serviceProviders/leafSlugs?${queryString.stringify({
    serviceProviderId,
  })}`, {
    headers: {
      'X-ANGI-CallerId': serviceProviderId,
    },
  }).then(response => response.body.leafSlugs.map(leaf => ({
    id: leaf.serviceProviderId,
    profileUrl: leaf.url.concat('?preview=true'),
  }))).catch(() => []);

const getLeafSlug =
  (serviceProviderIds: GetLeafSlugsRequest):
    Promise<{| body: any|}> =>
    getLeafSlugs(serviceProviderIds)
      .then(results => ({
        body: _.flatten(results).filter(x => x && x.id),
      }));

const getLeadServiceProvider = (serviceProviderId: string): Promise<{ leadServiceProvider: any }> =>
  httpClient.get(`/gateway/office-home/v1/angiLeads/${serviceProviderId}/info`, {
    headers: {
      'Content-Type': 'application/json',
      'X-ANGI-CallerType': 'ServiceProvider',
      'X-ANGI-CallerId': serviceProviderId,
    },
  });

const getSpProductPackage = (serviceProviderId: string): Promise<{ spProductPackage: any }> =>
  httpClient.get(`/gateway/lead/v1/angiLeads/${serviceProviderId}/leads/spp/get-all-user-attributes/419`, {
    headers: {
      'Content-Type': 'application/json',
      'X-ANGI-CallerType': 'ServiceProvider',
      'X-ANGI-CallerId': serviceProviderId,
    },
  });

export default {
  getAdsServiceProviderInfo,
  getCurrentUsersServiceProviders,
  getServiceProvider,
  getServiceProviderBusinessInfo,
  getServiceProviderUnreadCounts,
  getServiceProviderMetrics,
  getServiceProviderAccountOwner,
  getLeafSlug,
  getLeadServiceProvider,
  getSpProductPackage,
};



// WEBPACK FOOTER //
// ./src/app/common/serviceProvider/serviceProviderService.js